import type { TaskInputPrompts, TaskParams } from '@/app/types';
import { getReportParams } from './get-report-params';

interface GetDefaultReportParamsProps {
  value: TaskParams;
  isInspireMe: boolean;
  templateScene?: string;
  inspirationId?: string;
  isNewbieTask?: boolean;
}

function formatTtsPrompt(prompt: TaskInputPrompts): string {
  if (prompt.type !== 'text') {
    return '';
  }

  const content = prompt.content || '';
  const emotion = prompt.audio?.emotion;
  if (!emotion) {
    return content;
  }

  return `{${emotion}}${content}{/${emotion}}`;
}

// 默认上报参数
export const getDefaultReportParams = ({
  value,
  isInspireMe,
  templateScene,
  inspirationId,
  isNewbieTask,
}: GetDefaultReportParamsProps) => {
  // 提取提示文本
  const extractPromptText = () => {
    // 处理 multi_frame 类型
    if (value.type === 'multi_frame') {
      const multiFramePrompts = value.input.prompts
        ?.filter((p) => p.type === 'text' && p.multi_frame?.range)
        .sort((a, b) => (a.multi_frame?.range?.from ?? 0) - (b.multi_frame?.range?.from ?? 0));

      if (!multiFramePrompts || multiFramePrompts.length === 0) {
        return '';
      }

      const frameDurations = value.settings.frame_durations || [];
      return multiFramePrompts
        .map((p) => {
          const from = p.multi_frame?.range?.from ?? 0;
          const to = p.multi_frame?.range?.to ?? 0;
          const content = p.content || '';
          // 索引为 from - 1，因为 frame_durations 数组索引从 0 开始，而帧编号从 1 开始
          const duration = frameDurations[from] ?? 5;
          return `F${from + 1}-F${to + 1}：${content}，${duration}s;`;
        })
        .join('\n');
    }

    if (value.type === 'tts') {
      return value.input.prompts?.map(formatTtsPrompt).join('') || '';
    }

    if (value.type !== 'text2audio') {
      return value.input.prompts?.find((p) => p.type === 'text')?.content || '';
    }

    return (
      value.input.prompts
        ?.filter((p) => p.type === 'text')
        .map((p) => p.content)
        .join('\n') || ''
    );
  };
  const elementList = value.input.prompts?.filter((p) => p.type === 'material') || [];
  return {
    ...getReportParams(value.type, value.settings, templateScene),
    prompt: extractPromptText(),
    is_enhance_prompt: value.input.enhance || false,
    pro_mode: value.input.editor_mode === 'professional' || false,
    is_inspire_me: isInspireMe,
    limited_trial: value.settings.use_trial ?? false,
    inspiration_id: inspirationId || '',
    image_count: value.input.prompts?.filter((p) => p.type === 'image').flatMap((p) => p.src_imgs)?.length || 0,
    element_count: elementList.length || 0,
    sharedreference_count: elementList.filter((it) => it.material?.share_id).length || 0,
    amount: value.settings.sample_count || 1,
    encoding: value.settings.codec || '',
    timing_number: value.type === 'text2audio' ? value.input.prompts?.filter((p) => p.timing_text).length || 0 : 0,
    is_newbie_task: isNewbieTask || false,
    dubbing_type: value.settings.lip_sync ? 'sfx_and_voice' : value.settings.with_audio ? 'sfx' : 'no_dubbing',
  };
};
